import type { CSSProperties, ReactNode } from "react"
import Link from "next/link"
import type { BreadcrumbTrail } from "@/lib/breadcrumbs"

/**
 * Breadcrumb — the ONE visible breadcrumb renderer (FRIDGE-269, Phase 1).
 *
 * Accessible list semantics on every page:
 *   <nav aria-label="Breadcrumb"><ol><li>…</li><li aria-current="page">…</li></ol></nav>
 * The last crumb is the current page: never linked, marked aria-current.
 * Separators are decorative (aria-hidden).
 *
 * Feed it a `trail` from lib/breadcrumbs.ts — the same array the page hands
 * to buildBreadcrumbSchema / <BreadcrumbJsonLd>, so visible names and the
 * BreadcrumbList names cannot diverge.
 *
 * Look: the defaults reproduce the brand-kit house style exactly (uppercase
 * 12px, wide tracking, gray-500, "/" separators, orange hover) — the flex
 * container simply moved from the <nav> to the <ol>. Every class prop
 * REPLACES its default so other surfaces (the editorial `.ed-breadcrumb`
 * pages, the PDP) keep their own look while sharing the markup:
 *   variant="editorial" — the ed-root pages: put `.ed-breadcrumb` on the nav
 *   (its CSS carries the type, gap, wrap and margin) and a wrapping flex list.
 *
 * `items` ({ label, href }) is the legacy prop shape; it still renders so
 * untouched call sites compile, but every template is expected to pass
 * `trail` (tests/breadcrumbs-source-pin.test.ts lists the ones left).
 */

interface LegacyBreadcrumbItem {
  label: string
  href?: string
}

export interface BreadcrumbProps {
  /** The trail from lib/breadcrumbs.ts (preferred). */
  trail?: BreadcrumbTrail
  /** Legacy shape — mapped onto the trail; prefer `trail`. */
  items?: LegacyBreadcrumbItem[]
  /**
   * `kit` (default): the brand-kit look. `editorial`: the ed-root pages,
   * where `.ed-breadcrumb` on the nav styles the trail.
   */
  variant?: "kit" | "editorial"
  /** Classes on the <nav>. Replaces the variant default. */
  className?: string
  /** Inline style on the <nav> (the editorial pages set marginBottom here). */
  style?: CSSProperties
  /** Classes on the <ol> — the flex container. Replaces the variant default. */
  listClassName?: string
  /** Classes on every <li>. Replaces the variant default. */
  itemClassName?: string
  /** Classes on the linked (non-current) crumbs. Replaces the variant default. */
  linkClassName?: string
  /** Classes on the current-page crumb. Replaces the variant default. */
  currentClassName?: string
  /** Classes on the separator. Replaces the variant default. */
  separatorClassName?: string
  /** Separator node between crumbs (default "/"). */
  separator?: ReactNode
}

const VARIANT_DEFAULTS = {
  kit: {
    nav: "mb-5 text-xs uppercase tracking-widest text-gray-500 font-medium",
    list: "flex items-center gap-2 list-none m-0 p-0",
    item: "flex items-center gap-2",
    link: "hover:text-[#ff9951] transition-colors",
    current: "text-gray-900",
    separator: "text-gray-500",
  },
  editorial: {
    nav: "ed-breadcrumb",
    // nowrap: the trail is ONE line at every width — the current-page crumb
    // shrinks and ellipsizes; ancestor crumbs never break (owner, 08/28-29/2026:
    // at narrow widths the trail wrapped to a second line on release pages).
    list: "flex flex-nowrap items-center gap-2 list-none m-0 p-0 min-w-0",
    item: "flex items-center gap-2 min-w-0 max-w-full",
    link: "",
    current: "leaf",
    separator: "sep",
  },
} as const

export function Breadcrumb({
  trail,
  items,
  variant = "kit",
  className,
  style,
  listClassName,
  itemClassName,
  linkClassName,
  currentClassName,
  separatorClassName,
  separator = "/",
}: BreadcrumbProps) {
  const crumbs: BreadcrumbTrail =
    trail ?? (items ?? []).map((item) => ({ name: item.label, url: item.href }))
  if (crumbs.length === 0) return null

  const d = VARIANT_DEFAULTS[variant]
  const navClass = className ?? d.nav
  const listClass = listClassName ?? d.list
  const itemClass = itemClassName ?? d.item
  const linkClass = linkClassName ?? d.link
  const currentClass = currentClassName ?? d.current
  const separatorClass = separatorClassName ?? d.separator

  return (
    <nav aria-label="Breadcrumb" className={navClass || undefined} style={style}>
      <ol className={listClass || undefined}>
        {crumbs.map((crumb, i) => {
          const isCurrent = i === crumbs.length - 1
          return (
            <li
              key={`${i}-${crumb.name}`}
              // Ancestors keep their full name at EVERY width; the current-page
              // item is the flexible one, so a squeeze ellipsizes the leaf
              // instead of wrapping the trail. FRIDGE-1117 (owner circled
              // "B.. / BEST CAMPERVAN TOP FREEZER REFRIGE…" on his phone): the
              // parent used to give way first (shrink 4 to the leaf's 1), which
              // squeezed the one crumb that carries meaning ("Blog") to a single
              // letter so the leaf could keep 8ch. Now the parent never shrinks;
              // a genuinely long ancestor ("Appliance Price Index", a city name)
              // is capped by its own link at half the viewport and ellipsizes
              // itself, so the trail still never clips at the right edge
              // (FRIDGE-1031).
              className={`${itemClass || ""} ${
                isCurrent
                  ? "min-w-0"
                  : crumbs.length >= 3 && i < crumbs.length - 2
                    ? // On a phone only the parent and the leaf show; the home
                      // crumb and the middle ancestors leave the trail (the
                      // BreadcrumbList schema keeps the full path).
                      "hidden sm:flex flex-none"
                    : "flex-none"
              }`.trim() || undefined}
              aria-current={isCurrent ? "page" : undefined}
            >
              {i > 0 && (
                <span
                  // The parent's separator follows a crumb that is hidden on
                  // phones, so it hides with it (no leading "/").
                  className={`${separatorClass || ""} ${crumbs.length >= 3 && i === crumbs.length - 2 ? "hidden sm:inline" : ""}`.trim() || undefined}
                  aria-hidden="true"
                >
                  {separator}
                </span>
              )}
              {!isCurrent && crumb.url ? (
                <Link
                  href={crumb.url}
                  className={`${linkClass || ""} inline-block align-bottom truncate max-w-[50vw] sm:max-w-none`.trim()}
                  // WCAG 2.4.4 (FRIDGE-796): a collection crumb ("Side-by-Side
                  // Refrigerators" → /collections/…) shares its name with the
                  // chrome's hub link of the same type (→ /side-by-side-
                  // refrigerators) on every PDP — two purposes, one name. The
                  // accessible name says which one this is; the visible text
                  // and the BreadcrumbList schema are untouched (the parity
                  // test reads inner text, and schema names come from the
                  // trail, not the DOM).
                  aria-label={crumb.url.startsWith("/collections/") ? `${crumb.name} collection` : undefined}
                >
                  {crumb.name}
                </Link>
              ) : (
                // The current-page crumb truncates to ONE line (owner, 08/28/2026:
                // long release/product titles made trails "super busy", wrapping to
                // a second line). Full name stays in the BreadcrumbList schema and
                // in the hover title; ellipsis is presentation only.
                <span
                  className={currentClass || undefined}
                  title={crumb.name}
                  style={{
                    display: "inline-block",
                    // 100% = whatever width the flexed <li> resolves to, so the
                    // leaf truncates exactly at the available space; 560px caps
                    // it on wide screens. (Was 64vw — a viewport guess that let
                    // the trail wrap before the leaf gave way.)
                    maxWidth: "min(100%, 560px)",
                    // Never less than a few characters: a fully squeezed leaf
                    // rendered as a lone "…" that read as a "." (FRIDGE-1031).
                    minWidth: "8ch",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "bottom",
                  }}
                >
                  {crumb.name}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
